"use client";

import ResourceCard from "@/app/(core)/(home)/components/ResourceCard";
import PageSection, {
  PageSectionVariant,
} from "@/app/(core)/components/PageSection";
import SectionHeader from "@/app/(core)/components/SectionHeader";
import type { Resources } from "@/types/subject-schema";
import { Grid } from "@mantine/core";
import classes from "./everything-you-need-section.module.css";

interface EverythingYouNeedSectionProps {
  title?: string;
  subTitle?: string;
  firstSection?: boolean;
  variant?: PageSectionVariant;
}

export default function EverythingYouNeedSection({
  title,
  subTitle,
  firstSection = false,
  variant = "dark",
}: EverythingYouNeedSectionProps) {
  return (
    <PageSection id="resources" variant={variant} firstSection={firstSection}>
      <SectionHeader
        id="resources"
        title={title ?? "Everything You Need To Succeed."}
        subTitle={
          subTitle ?? "Built by examiners and teachers. Uncompromised quality."
        }
        align="center"
        needsLightTitle={variant === "dark"}
      />
      <Grid className={classes.resourcesGrid} gutter="lg">
        {RESOURCES.map((res, index) => (
          <Grid.Col key={index} span={{ base: 12, sm: 6, md: 4 }}>
            <ResourceCard
              resource={res.resource}
              title={res.title}
              badge={res.badge}
              description={res.description}
              highlight={res.highlight}
            />
          </Grid.Col>
        ))}
        <Grid.Col
          key={"overviewResourceCard"}
          span={{ base: 12, sm: 6, md: 4 }}
        >
          <ResourceCard
            resource={"syllabus"}
            title={"+ More Resources"}
            description="Everything you need to sail towards that 7 in the IB exams."
            highlight={true}
          />
        </Grid.Col>
      </Grid>
    </PageSection>
  );
}

const RESOURCES: {
  resource: Resources;
  title: string;
  badge?: string;
  description: string;
  highlight: boolean;
}[] = [
  {
    resource: "videos",
    title: "Videos",
    badge: "400+",
    description:
      "Expert-led animated tutorials. Understand complex topics immediately.",
    highlight: false,
  },
  {
    resource: "notes",
    title: "Notes",
    badge: "800+",
    description:
      "Examiner-written course notes with formulae and diagrams. Learn with ease.",
    highlight: false,
  },
  {
    resource: "definitions",
    title: "Definitions",
    badge: "850+",
    description:
      "Key terminology and IB-specific terms. Grasp exam-specific wording.",
    highlight: false,
  },
  {
    resource: "flashcards",
    title: "Flashcards",
    badge: "3,700+",
    description:
      "Key concepts with shuffle and reverse modes. Maximize retention before exams.",
    highlight: false,
  },
  {
    resource: "questions",
    title: "Questions",
    badge: "5,400+",
    description:
      "All formats, with examiner-approved mark schemes. Master your practice.",
    highlight: false,
  },
  {
    resource: "cram sheets",
    title: "Cram Sheets",
    badge: "200+",
    description:
      "Condensed summaries for continuous bite-sized revision. Nail revision in a pinch.",
    highlight: false,
  },
  {
    resource: "mock exams",
    title: "Mock Exams",
    description:
      "Generate endless exam papers. Build confidence under exam conditions.",
    highlight: false,
  },
  {
    resource: "ia examples",
    title: "IA Examples",
    description:
      "Scored exemplars from recent IB exams. Master your IA coursework.",
    highlight: false,
  },
];
